import React, {
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  type KeyboardEventHandler,
} from 'react';
import {
  Checkbox as CheckboxRAC,
  NumberField as NumberFieldRAC,
  Group as GroupRAC,
  Button as ButtonRAC,
  Input as InputRAC,
} from 'react-aria-components';

import { useConfig } from '../../contexts/config-context';
import { useFocusFieldEvent } from '../../contexts/events-context';
import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { useTranslation } from '../../contexts/translation-context';
import { SelectMode } from '../../field-settings/settings-shared/options/options-list/types';
import {
  type ProductListValue,
  type ProductValue,
} from '../../field-settings/settings-shared/products/types';
import { getSelectMode } from '../../fields/product-list/product-list-settings/select-mode-setting/get-select-mode';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { getImageURL } from '../../services/get-image-url';
import { type Option } from '../../types';
import { formatCurrency } from '../../utils/format-currency';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';
import { FieldsetHeadless } from '../fieldset/fieldset-headless';
import { getCurrencyAriaLabel } from '../shared/payment-field-utils';

import { type ProductListComponentProps } from './product-list-props';

export type ProductListProps = {
  children: ReactNode;
};

export const ProductList = ({ children }: ProductListProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    onBlur,
    onFocus,
    hasError,
    validation,
    FieldLayout,
  } = useFieldPropsV2<ProductListComponentProps>();

  const { inputId, labelId, ariaDescribedBy } = useFieldAttributes();

  const labelElement = findChildOfType(children, ProductList.Label);
  const optionsElement = findChildOfType(children, ProductList.Options);
  const descriptionElement = findChildOfType(children, ProductList.Description);
  const errorElement = findChildOfType(children, ProductList.Error);

  const _value = useMemo<ProductListValue>(() => value || [], [value]);

  const selectOptionsMode = getSelectMode(
    validation?.predefined?.paymentOptions,
  );
  const allowMany = selectOptionsMode === SelectMode.many;

  const handleChange = useCallback(
    (productValue: ProductValue, isChecked: boolean) => {
      const restProductValues = _value.filter(
        (val) => val.productId !== productValue.productId,
      );

      const newProductValues = isChecked
        ? [...restProductValues, productValue]
        : restProductValues;
      onChange?.(newProductValues);
    },
    [_value, onChange],
  );

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <FieldsetHeadless
          id={inputId}
          ariaLabelledBy={labelId}
          ariaDescribedBy={ariaDescribedBy}
          required={required}
          onBlur={onBlur}
          onFocus={onFocus}
        >
          {optionsElement
            ? applyInternalProps<OptionsInternalProps>(optionsElement, {
                allowMany,
                productValues: _value,
                onProductChange: handleChange,
                disabled,
                hasError,
              })
            : null}
        </FieldsetHeadless>
      )}
      renderDescription={() => (
        <>
          {errorElement}
          {descriptionElement}
        </>
      )}
    />
  );
};

type OptionsProps = {
  children: ReactNode;
  className?: string;
};

type OptionsInternalProps = {
  allowMany: boolean;
  productValues: ProductListValue;
  onProductChange: (productValue: ProductValue, isChecked: boolean) => void;
  disabled?: boolean;
  hasError?: boolean;
};

const Options = ({ children, className, ...internal }: OptionsProps) => {
  const { allowMany, productValues, onProductChange, disabled, hasError } =
    internal as OptionsInternalProps;
  const { id, options, validation, imageResize } =
    useFieldPropsV2<ProductListComponentProps>();
  const { currency } = useConfig();
  const { t } = useTranslation();

  const optionElement = findChildOfType(children, ProductList.Option);
  const firstCheckboxRef = useRef<HTMLLabelElement>(null);

  useFocusFieldEvent(() => {
    firstCheckboxRef.current?.focus({ preventScroll: true });
  }, id);

  return (
    <div className={className}>
      {options?.map((option, index) => {
        const price =
          validation?.predefined?.paymentOptions?.products?.find(
            (p) => p.id === option.value,
          )?.fixedPriceOptions?.price || '0';
        const currencyTranslation = getCurrencyAriaLabel(t, currency);
        const preselectedProduct = productValues.find(
          (i: ProductValue) => i.productId === option.value,
        );

        return applyInternalProps<OptionInternalProps>(optionElement, {
          key: option.id || option.value || option.label,
          option,
          price,
          quantity: preselectedProduct?.quantity,
          isSelected: Boolean(preselectedProduct),
          allowMany,
          onProductChange,
          disabled,
          hasError,
          currencyAriaLabel: currencyTranslation,
          imageResize,
          isFirst: index === 0,
          checkboxRef: index === 0 ? firstCheckboxRef : undefined,
        });
      })}
    </div>
  );
};

type OptionProps = {
  children: ReactNode;
  className?: string;
};

type OptionInternalProps = {
  option: Option;
  price: string;
  quantity?: number;
  isSelected: boolean;
  allowMany: boolean;
  onProductChange: (productValue: ProductValue, isChecked: boolean) => void;
  disabled?: boolean;
  hasError?: boolean;
  currencyAriaLabel: string;
  imageResize: ProductListComponentProps['imageResize'];
  isFirst?: boolean;
  checkboxRef?: React.RefObject<HTMLLabelElement>;
};

const ProductOption = ({ children, className, ...internal }: OptionProps) => {
  const {
    option,
    price,
    quantity = 0,
    isSelected,
    allowMany,
    onProductChange,
    disabled,
    hasError,
    currencyAriaLabel,
    imageResize,
    checkboxRef,
  } = internal as OptionInternalProps;

  const imageElement = findChildOfType(children, ProductOption.Image);
  const labelElement = findChildOfType(children, ProductOption.Label);
  const priceElement = findChildOfType(children, ProductOption.Price);
  const inputElement = findChildOfType(children, ProductOption.Input);
  const quantityElement = findChildOfType(children, ProductOption.Quantity);

  const handleCheckboxChange = useCallback(
    (isChecked: boolean) => {
      onProductChange(
        {
          productId: option.value!,
          price,
          quantity: 1,
        },
        isChecked,
      );
    },
    [onProductChange, option.value, price],
  );

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      onProductChange(
        {
          productId: option.value!,
          price,
          quantity: newQuantity,
        },
        newQuantity > 0,
      );
    },
    [onProductChange, option.value, price],
  );

  const ariaLabel = `${option.label} ${currencyAriaLabel} ${price}`;

  if (allowMany) {
    return (
      <div className={className} data-hook="product">
        {imageElement
          ? applyInternalProps<OptionImageInternalProps>(imageElement, {
              option,
              imageResize,
            })
          : null}
        {labelElement
          ? applyInternalProps<OptionLabelInternalProps>(labelElement, {
              label: option.label,
            })
          : null}
        {priceElement
          ? applyInternalProps<OptionPriceInternalProps>(priceElement, {
              price,
            })
          : null}
        {quantityElement
          ? applyInternalProps<OptionQuantityInternalProps>(quantityElement, {
              quantity,
              onQuantityChange: handleQuantityChange,
              disabled,
              hasError,
              ariaLabel,
            })
          : null}
      </div>
    );
  }

  return (
    <CheckboxRAC
      ref={checkboxRef}
      isSelected={isSelected}
      onChange={handleCheckboxChange}
      isDisabled={disabled}
      isInvalid={hasError}
      aria-label={ariaLabel}
      className={className}
      data-hook="product"
    >
      {imageElement
        ? applyInternalProps<OptionImageInternalProps>(imageElement, {
            option,
            imageResize,
          })
        : null}
      {labelElement
        ? applyInternalProps<OptionLabelInternalProps>(labelElement, {
            label: option.label,
          })
        : null}
      {priceElement
        ? applyInternalProps<OptionPriceInternalProps>(priceElement, {
            price,
          })
        : null}
      {inputElement}
    </CheckboxRAC>
  );
};

type OptionImageProps = {
  className?: string;
};

type OptionImageInternalProps = {
  option: Option;
  imageResize: ProductListComponentProps['imageResize'];
};

const OptionImage = ({ className, ...internal }: OptionImageProps) => {
  const { option } = internal as OptionImageInternalProps;

  if (!option.media?.image) {
    return null;
  }

  const imageURL = getImageURL(option?.media?.image, 48, 56);

  if (!imageURL) {
    return null;
  }

  const imageAlt = option.media.image.altText || option.label || '';

  return (
    <img
      className={className}
      src={imageURL}
      alt={imageAlt}
      data-hook="product-image"
    />
  );
};

type OptionLabelProps = {
  className?: string;
};

type OptionLabelInternalProps = {
  label: string;
};

const OptionLabel = ({ className, ...internal }: OptionLabelProps) => {
  const { label } = internal as OptionLabelInternalProps;

  return (
    <span className={className} data-hook="product-label">
      {label || ''}
    </span>
  );
};

type OptionPriceProps = {
  className?: string;
};

type OptionPriceInternalProps = {
  price: string;
};

const OptionPrice = ({ className, ...internal }: OptionPriceProps) => {
  const { price } = internal as OptionPriceInternalProps;
  const { currency, regionalFormat } = useConfig();

  const formattedPrice = formatCurrency({
    amount: price,
    currency: currency?.code,
    locale: regionalFormat,
  });

  return (
    <span className={className} data-hook="product-price">
      {formattedPrice}
    </span>
  );
};

type OptionInputProps = {
  className?: string;
};

const OptionInput = ({ className }: OptionInputProps) => {
  return <div aria-hidden="true" className={className} />;
};

type OptionQuantityProps = {
  className?: string;
};

type OptionQuantityInternalProps = {
  quantity: number;
  onQuantityChange: (quantity: number) => void;
  disabled?: boolean;
  hasError?: boolean;
  ariaLabel: string;
};

const OptionQuantity = ({ className, ...internal }: OptionQuantityProps) => {
  const { quantity, onQuantityChange, disabled, hasError, ariaLabel } =
    internal as OptionQuantityInternalProps;

  const handleChange = useCallback(
    (value: number) => {
      onQuantityChange(value < 0 ? 0 : value);
    },
    [onQuantityChange],
  );

  const onKeyDown: KeyboardEventHandler<HTMLElement> = useCallback(
    (event) => {
      if (event.key === 'Home') {
        event.preventDefault();
        handleChange(0);
      }
    },
    [handleChange],
  );

  return (
    <NumberFieldRAC
      value={quantity}
      onChange={handleChange}
      minValue={0}
      step={1}
      isDisabled={disabled}
      isInvalid={hasError}
      aria-label={ariaLabel}
      className={className}
      data-hook="product-quantity"
    >
      <GroupRAC onKeyDown={onKeyDown}>
        <ButtonRAC slot="decrement">-</ButtonRAC>
        <InputRAC />
        <ButtonRAC slot="increment">+</ButtonRAC>
      </GroupRAC>
    </NumberFieldRAC>
  );
};

ProductOption.Image = OptionImage;
ProductOption.Label = OptionLabel;
ProductOption.Price = OptionPrice;
ProductOption.Input = OptionInput;
ProductOption.Quantity = OptionQuantity;

Options.Option = ProductOption;

ProductList.Label = Label;
ProductList.Options = Options;
ProductList.Option = ProductOption;
ProductList.Description = Description;
ProductList.Error = Error;
